import React, { Component } from 'react';

import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Carousel } from 'antd';
import ReactGA from 'react-ga4';

import { 
  CampingSite, 
  getCampingSites, 
  getStates,
  getCities, 
  getCountries,
  filterByState, 
  filterByCity, 
  filterByCountry,
  filterByName 
} from '../../../services/campingData';

import { MapWrapper } from '../map';
import { Map } from '@utils';
import MapMarker from '@assets/images/marker.svg';
import missingImage from '@assets/images/campings/rv-missing-image.jpg';
import memberDealImage from '@assets/images/campings/memberdeal.png';
import { ILoginState } from '@share/store/slices';
import { Routes } from '@share/constants';
import {
  CAMPINGS_FILTER_COUNTRY,
  CAMPINGS_FILTER_STATE,
  CAMPINGS_FILTER_CITY,
  CAMPINGS_FILTER_SEARCH,
  CAMPINGS_FILTER_EXCLUDE_NO_PHOTOS,
  CAMPINGS_FILTER_EXCLUDE_NO_WEBSITE,
  CAMPINGS_RESET_FILTERS,
  CAMPINGS_CLICK_RESORT,
  CAMPINGS_COPY_MEMBER_CODE,
  CAMPINGS_VIEW_DETAILS_TAB,
  CAMPINGS_VIEW_SUMMARY_TAB,
  CAMPINGS_VISIT_WEBSITE,
  CAMPINGS_CAROUSEL_NAVIGATE,
  CAMPINGS_SHOW_MORE_DESCRIPTION,
  CAMPINGS_SHOW_LESS_DESCRIPTION,
  CAMPINGS_OPEN_MEMBER_CODE_POPUP,
  CAMPINGS_CLOSE_MEMBER_CODE_POPUP,
  CAMPINGS_VIEW_MAP,
} from '@share/constants';

import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IProps extends WrappedComponentProps, RouteComponentProps, IMapStateToProps {}

interface IState {
  selectedState: string;
  selectedCity: string;
  selectedCountry: string;
  searchTerm: string;
  campingSites: CampingSite[];
  allSites: CampingSite[];
  states: string[];
  cities: string[];
  countries: string[];
  loading: boolean;
  copiedCode: string | null;
  expandedAmenities: string | null;
  expandedDetails: string | null;
  activeTab: { [key: string]: string };
  expandedDescriptions: { [key: string]: boolean };
  showMemberCodePopup: { [key: string]: boolean };
  imageIndex: { [key: string]: number };
  excludeNoPhotos: boolean;
  excludeNoWebsite: boolean;
}

class CampingsComponent extends Component<IProps, IState> {

  state: IState = { 
    selectedState: 'All States', 
    selectedCity: 'All Cities',
    selectedCountry: 'All Countries',
    searchTerm: '',
    campingSites: [],
    allSites: [],
    states: [],
    cities: [],
    countries: [],
    loading: true,
    copiedCode: null,
    expandedAmenities: null,
    expandedDetails: null,
    activeTab: {},
    expandedDescriptions: {},
    showMemberCodePopup: {},
    imageIndex: {},
    excludeNoPhotos: false,
    excludeNoWebsite: false
  };

  async componentDidMount() {
    await this.loadCampingData();
    
    // Scroll to top after data loads
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    // Request notification permission for clipboard copy notifications
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  trackEvent = (action: string, label: string, _value?: string) => {
    const { loginStore } = this.props;
    const { account } = loginStore;
    
    if (account) {
      ReactGA.event({
        category: account.name as string,
        action: `${action}_${account.name.toUpperCase()}`,
        label,
        nonInteraction: false,
      });
    }
  };

  loadCampingData = async () => {
    try {
      this.setState({ loading: true });
      let sites = await getCampingSites();
      
      // Remove duplicates based on ID (and title as fallback) when loading initial data
      const seen = new Set<string>();
      sites = sites.filter(site => {
        const key = site.id || site.title;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      
      const states = getStates(sites);
      const cities = getCities(sites);
      const countries = getCountries(sites);
      
      this.setState({ 
        campingSites: sites, 
        allSites: sites,
        states: ['All States', ...states],
        cities: ['All Cities', ...cities],
        countries: ['All Countries', ...countries],
        loading: false 
      });
    } catch (error) {
      console.error('Error loading camping data:', error);
      this.setState({ loading: false });
    }
  };


  // Helper function to check if a site has valid photos
  hasValidPhotos = (site: CampingSite): boolean => {
    const isValidImageUrl = (url: string): boolean => {
      const trimmed = url.trim();
      return trimmed !== '' && (trimmed.startsWith('http://') || trimmed.startsWith('https://'));
    };
    
    // Check main photo
    if (site.photo && isValidImageUrl(site.photo)) {
      return true;
    }
    
    // Check additional photos
    if (site.photos && Array.isArray(site.photos) && site.photos.length > 0) {
      return site.photos.some(photo => photo && isValidImageUrl(photo));
    }
    
    return false;
  };

  // Helper function to check if a site has a valid website
  hasValidWebsite = (site: CampingSite): boolean => {
    return !!site.website && site.website.trim() !== '' && 
           (site.website.startsWith('http://') || site.website.startsWith('https://'));
  };

  getFilteredSites = (): CampingSite[] => {
    const { selectedState, selectedCity, selectedCountry, searchTerm, allSites, excludeNoPhotos, excludeNoWebsite } = this.state;
    
    let filtered = [...allSites];
    
    // Filter by country
    if (selectedCountry && selectedCountry !== 'All Countries') {
      filtered = filterByCountry(filtered, selectedCountry);
    }
    
    // Filter by state
    if (selectedState && selectedState !== 'All States') {
      filtered = filterByState(filtered, selectedState);
    }
    
    // Filter by city
    if (selectedCity && selectedCity !== 'All Cities') {
      filtered = filterByCity(filtered, selectedCity);
    }
    
    // Search by name
    if (searchTerm) {
      filtered = filterByName(filtered, searchTerm);
    }
    
    // Exclude resorts with no photos (if checked)
    // This works accumulatively with all previous filters (country, state, city, search)
    if (excludeNoPhotos) {
      filtered = filtered.filter(site => this.hasValidPhotos(site));
    }
    
    // Exclude resorts with no website (if checked)
    // This works accumulatively with all previous filters including excludeNoPhotos
    // If both exclusions are checked, sites must have BOTH photos AND website
    if (excludeNoWebsite) {
      filtered = filtered.filter(site => this.hasValidWebsite(site));
    }
    
    // Remove duplicates based on ID (and title as fallback)
    const seen = new Set<string>();
    filtered = filtered.filter(site => {
      const key = site.id || site.title;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    
    // Sort alphabetically by title
    filtered.sort((a, b) => {
      const titleA = a.title.toLowerCase();
      const titleB = b.title.toLowerCase();
      return titleA.localeCompare(titleB);
    });
    
    return filtered;
  };

  handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = event.target.value;
    this.setState({ 
      selectedCountry,
      selectedState: 'All States', // Reset state when country changes
      selectedCity: 'All Cities' // Reset city when country changes
    });
    
    if (selectedCountry !== 'All Countries') {
      this.trackEvent(CAMPINGS_FILTER_COUNTRY, `Filtered by country: ${selectedCountry}`, selectedCountry);
    }
  };

  handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedState = event.target.value;
    this.setState({ 
      selectedState,
      selectedCity: 'All Cities' // Reset city when state changes
    });
    
    if (selectedState !== 'All States') {
      this.trackEvent(CAMPINGS_FILTER_STATE, `Filtered by state: ${selectedState}`, selectedState);
    }
  };

  handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCity = event.target.value;
    this.setState({ selectedCity });
    
    if (selectedCity !== 'All Cities') {
      this.trackEvent(CAMPINGS_FILTER_CITY, `Filtered by city: ${selectedCity}`, selectedCity);
    }
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    this.setState({ searchTerm });
    
    if (searchTerm && searchTerm.trim().length > 0) {
      this.trackEvent(CAMPINGS_FILTER_SEARCH, `Searched for: ${searchTerm}`, searchTerm);
    }
  };

  handleExcludeNoPhotosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const excludeNoPhotos = event.target.checked;
    this.setState({ excludeNoPhotos });
    
    this.trackEvent(
      CAMPINGS_FILTER_EXCLUDE_NO_PHOTOS, 
      `Exclude no photos: ${excludeNoPhotos ? 'enabled' : 'disabled'}`
    );
  };

  handleExcludeNoWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const excludeNoWebsite = event.target.checked;
    this.setState({ excludeNoWebsite });
    
    this.trackEvent(
      CAMPINGS_FILTER_EXCLUDE_NO_WEBSITE, 
      `Exclude no website: ${excludeNoWebsite ? 'enabled' : 'disabled'}`
    );
  };

  resetFilters = () => {
    this.setState({
      selectedCountry: 'All Countries',
      selectedState: 'All States',
      selectedCity: 'All Cities',
      searchTerm: '',
      excludeNoPhotos: false,
      excludeNoWebsite: false
    });
    
    this.trackEvent(CAMPINGS_RESET_FILTERS, 'User reset all filters');
  };

         copyCouponCode = async (couponCode: string, siteId: string) => {
           try {
             await navigator.clipboard.writeText(couponCode);
             this.setState({ copiedCode: siteId });

             // Clear the copied state after 2 seconds
             setTimeout(() => {
               this.setState({ copiedCode: null });
             }, 2000);
           } catch (err) {
             console.error('Failed to copy coupon code:', err);
             // Fallback for older browsers
             const textArea = document.createElement('textarea');
             textArea.value = couponCode;
             document.body.appendChild(textArea);
             textArea.select();
             document.execCommand('copy');
             document.body.removeChild(textArea);

             this.setState({ copiedCode: siteId });
             setTimeout(() => {
               this.setState({ copiedCode: null });
             }, 2000);
           }
         };

  toggleAmenities = (siteTitle: string) => {
    this.setState(prevState => ({
      expandedAmenities: prevState.expandedAmenities === siteTitle ? null : siteTitle
    }));
  };

  toggleDetails = (siteTitle: string) => {
    this.setState(prevState => ({
      expandedDetails: prevState.expandedDetails === siteTitle ? null : siteTitle
    }));
  };

  setActiveTab = (siteTitle: string, tabName: string) => {
    this.setState(prevState => ({
      activeTab: {
        ...prevState.activeTab,
        [siteTitle]: tabName
      }
    }));
    
    const action = tabName === 'location' ? CAMPINGS_VIEW_DETAILS_TAB : tabName === 'deal' ? CAMPINGS_VIEW_DETAILS_TAB : CAMPINGS_VIEW_SUMMARY_TAB;
    this.trackEvent(action, `Clicked ${tabName} tab for resort: ${siteTitle}`, siteTitle);
    
    // Track resort click when opening location tab
    if (tabName === 'location') {
      this.trackEvent(CAMPINGS_CLICK_RESORT, `Opened resort location: ${siteTitle}`, siteTitle);
    }
  };

  toggleDescription = (siteTitle: string) => {
    const isExpanded = this.state.expandedDescriptions[siteTitle];
    this.setState(prevState => ({
      expandedDescriptions: {
        ...prevState.expandedDescriptions,
        [siteTitle]: !isExpanded
      }
    }));
    
    const action = isExpanded ? CAMPINGS_SHOW_LESS_DESCRIPTION : CAMPINGS_SHOW_MORE_DESCRIPTION;
    this.trackEvent(action, `${isExpanded ? 'Collapsed' : 'Expanded'} description for: ${siteTitle}`, siteTitle);
  };

  toggleMemberCodePopup = (siteTitle: string) => {
    const isOpen = this.state.showMemberCodePopup[siteTitle];
    this.setState(prevState => ({
      showMemberCodePopup: {
        ...prevState.showMemberCodePopup,
        [siteTitle]: !isOpen
      }
    }));
    
    const action = isOpen ? CAMPINGS_CLOSE_MEMBER_CODE_POPUP : CAMPINGS_OPEN_MEMBER_CODE_POPUP;
    this.trackEvent(action, `${isOpen ? 'Closed' : 'Opened'} member code popup for: ${siteTitle}`, siteTitle);
  };

  renderImageCarousel = (site: CampingSite) => {
    // Helper function to check if URL is valid for browsers (only HTTP/HTTPS)
    const isValidImageUrl = (url: string): boolean => {
      if (!url) return false;
      const trimmed = url.trim();
      return trimmed !== '' && (trimmed.startsWith('http://') || trimmed.startsWith('https://'));
    };
    
    // Collect all images: main photo + additional photos
    const allImages: string[] = [];
    
    // Add main photo if it exists and is valid (skip image:// URLs)
    if (site.photo && isValidImageUrl(site.photo)) {
      allImages.push(site.photo.trim());
    }
    
    // Add additional photos if they exist (skip image:// URLs)
    if (site.photos && Array.isArray(site.photos) && site.photos.length > 0) {
      site.photos.forEach(photo => {
        if (photo && isValidImageUrl(photo)) {
          allImages.push(photo.trim());
        }
      });
    }
    
    // If no images, show placeholder
    if (allImages.length === 0) {
      return (
        <div className="campings__site-placeholder">
          <img src={missingImage} alt={site.title} />
        </div>
      );
    }
    
    // If only one image, show it directly (no carousel needed)
    if (allImages.length === 1) {
      return (
        <img 
          src={allImages[0]} 
          alt={site.title}
          loading="eager"
          onError={(e) => {
            e.currentTarget.src = missingImage;
          }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      );
    }
    
    // Multiple images: show carousel
    const carouselProps = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      arrows: true,
      lazyLoad: false as const,
      afterChange: (current: number) => {
        // Track carousel navigation
        this.trackEvent(
          CAMPINGS_CAROUSEL_NAVIGATE, 
          `Navigated carousel to image ${current + 1} for: ${site.title}`, 
          site.title
        );
      },
    };
    
    return (
      <Carousel {...carouselProps as any} className="campings__image-carousel">
        {allImages.map((imageUrl, index) => (
          <div key={index}>
            <img 
              src={imageUrl} 
              alt={`${site.title} - Image ${index + 1}`}
              onError={(e) => {
                e.currentTarget.src = missingImage;
              }}
              loading="eager"
              className="campings__carousel-image"
            />
          </div>
        ))}
      </Carousel>
    );
  };

  copyMemberCode = (siteTitle: string) => {
    const memberCode = 'XWYZZ'; // Updated member code
    navigator.clipboard.writeText(memberCode).then(() => {
      // Show native browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Code Copied!', {
          body: `Member code "${memberCode}" copied to clipboard`,
          icon: '/favicon.ico'
        });
      } else {
        // Fallback: show a temporary alert
        const originalText = document.title;
        document.title = `✓ Copied: ${memberCode}`;
        setTimeout(() => {
          document.title = originalText;
        }, 2000);
      }
      
      this.trackEvent(CAMPINGS_COPY_MEMBER_CODE, `Copied member code for: ${siteTitle}`, siteTitle);
      
      this.setState({ copiedCode: siteTitle });
      setTimeout(() => {
        this.setState({ copiedCode: null });
      }, 2000);

      // Close the popup after copying
      this.setState(prevState => ({
        showMemberCodePopup: {
          ...prevState.showMemberCodePopup,
          [siteTitle]: false
        }
      }));
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = memberCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show fallback notification
      const originalText = document.title;
      document.title = `✓ Copied: ${memberCode}`;
      setTimeout(() => {
        document.title = originalText;
      }, 2000);
      
      this.trackEvent(CAMPINGS_COPY_MEMBER_CODE, `Copied member code for: ${siteTitle}`, siteTitle);
      
      this.setState({ copiedCode: siteTitle });
      setTimeout(() => {
        this.setState({ copiedCode: null });
      }, 2000);

      // Close the popup after copying
      this.setState(prevState => ({
        showMemberCodePopup: {
          ...prevState.showMemberCodePopup,
          [siteTitle]: false
        }
      }));
    });
  };

  render(): React.ReactNode {
    const { selectedState, selectedCity, selectedCountry, searchTerm, loading, states, cities, countries, activeTab, showMemberCodePopup, excludeNoPhotos, excludeNoWebsite } = this.state;
    const filteredSites = this.getFilteredSites();
    const accountUsername = (this.props.match.params as any)?.accountUsername || '';

    return (
      <div className="campings">
        {/* Hero Section with Single Image */}
        <div className="campings__hero">
          <div className="campings__hero-content">
            <h1 className="campings__hero-title">
              <FormattedMessage id="campings.hero.title" defaultMessage="The Ultimate Camping Network" />
            </h1>
            <p className="campings__hero-subtitle">
              <FormattedMessage id="campings.hero.subtitle" defaultMessage="Access to over 140 resorts across the nation with friendly, first-class service" />
            </p>
          </div>
        </div>

        {/* Filter Section - Replaced Search with Filters */}
        <div className="campings__filter-section">
          <div className="container">
            <div className="campings__filter-controls">
              <div className="campings__filter-group">
                  <label>Filter by Country:</label>
                <select value={selectedCountry} onChange={this.handleCountryChange}>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Filter by State:</label>
                <select value={selectedState} onChange={this.handleStateChange}>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Filter by City:</label>
                <select value={selectedCity} onChange={this.handleCityChange}>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Search by Name:</label>
                <input
                  type="text"
                  placeholder="Search by resort name..."
                  value={searchTerm}
                  onChange={this.handleSearchChange}
                />
              </div>
              <div className="campings__filter-group">
                <label>
                  <input
                    type="checkbox"
                    checked={excludeNoPhotos}
                    onChange={this.handleExcludeNoPhotosChange}
                  />
                  Exclude resorts with no photos
                </label>
              </div>
              <div className="campings__filter-group">
                <label>
                  <input
                    type="checkbox"
                    checked={excludeNoWebsite}
                    onChange={this.handleExcludeNoWebsiteChange}
                  />
                  Exclude resorts with no website
                </label>
              </div>
            </div>
            <div className="campings__results-count">
              <div className="campings__results-text">
                Showing {filteredSites.length} of {this.state.allSites.length} camping sites
              </div>
              {(selectedCountry !== 'All Countries' || selectedState !== 'All States' || selectedCity !== 'All Cities' || searchTerm || excludeNoPhotos || excludeNoWebsite) && (
                <button 
                  className="campings__reset-filters"
                  onClick={this.resetFilters}
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Camping Sites Grid */}
        <div id="campings-sites-section" className="campings__sites-section">
          <div className="container">
            <div className="campings__sites-header">
              <h2><FormattedMessage id="campings.sites.title" defaultMessage="Explore Resorts Across The USA, Canada, and Mexico" /></h2>
              <p><FormattedMessage id="campings.sites.subtitle" defaultMessage="Experience our affiliated camping system and enjoy resorts with an array of activities and amenities. Use the cupons below to access your benefits with each resort" /></p>
            </div>

            {loading ? (
              <div className="campings__loading">
                <p><FormattedMessage id="campings.loading" defaultMessage="Loading camping sites..." /></p>
              </div>
            ) : (
              <div className="campings__sites-grid">
                {filteredSites.map(site => (
                <div key={site.id} className="campings__site-card">
                  {/* Member Code Popup */}
                  {showMemberCodePopup[site.title] && (
                    <div className="campings__member-code-popup">
                      <div className="campings__member-code-content">
                        <div className="campings__member-code-header">
                          <h3>Exclusive Member Deal</h3>
                          <button 
                            className="campings__member-code-close"
                            onClick={() => this.toggleMemberCodePopup(site.title)}
                          >
                            ×
                          </button>
                        </div>
                        <div className="campings__member-code-body">
                          <p>As a valued member, you get exclusive access to our premium camping experience</p>
                          <div className="campings__member-code-section">
                            <span className="campings__member-code-label">Use Code:</span>
                            <span className="campings__member-code-value">XWYZZ</span>
                          </div>
                          <button 
                            className="campings__member-code-copy"
                            onClick={() => this.copyMemberCode(site.title)}
                          >
                            Copy Code
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="campings__site-image">
                    {this.renderImageCarousel(site)}
                    {site.featured && (
                      <div className="campings__site-featured">Featured</div>
                    )}
                  </div>
                  <div className="campings__site-content">
                    <h3 title={site.title}>{site.title}</h3>
                    
                    {/* Tab Navigation */}
                    <div className="campings__tabs">
                      <button
                        className={`campings__tab ${(activeTab[site.title] || 'summary') === 'summary' ? 'active' : ''}`}
                        onClick={() => this.setActiveTab(site.title, 'summary')}
                      >
                        Summary
                      </button>
                      <button
                        className={`campings__tab ${activeTab[site.title] === 'location' ? 'active' : ''}`}
                        onClick={() => this.setActiveTab(site.title, 'location')}
                      >
                        Location
                      </button>
                      <button
                        className={`campings__tab ${activeTab[site.title] === 'deal' ? 'active' : ''}`}
                        onClick={() => this.setActiveTab(site.title, 'deal')}
                      >
                        Deal
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="campings__tab-content">
                      {(activeTab[site.title] || 'summary') === 'summary' ? (
                        <div className="campings__summary-tab">
                          {/* Long Description */}
                          <div className="campings__description">
                            <p 
                              className="campings__site-description"
                              title={site.longDescription ? site.longDescription.replace(/<[^>]*>/g, '') : 'Beautiful camping destination with great amenities.'}
                            >
                              {(() => {
                                const description = site.longDescription || 'Beautiful camping destination with great amenities.';
                                let cleanDescription = description.replace(/<[^>]*>/g, '');
                                // Remove NBSP and other HTML entities
                                cleanDescription = cleanDescription.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                                // Clean up multiple spaces
                                cleanDescription = cleanDescription.replace(/\s+/g, ' ').trim();
                                const words = cleanDescription.split(' ');
                                const isExpanded = this.state.expandedDescriptions[site.title];
                                
                                if (words.length <= 62 || isExpanded) {
                                  return cleanDescription;
                                } else {
                                  return words.slice(0, 62).join(' ') + '...';
                                }
                              })()}
                            </p>
                            {(() => {
                              const description = site.longDescription || 'Beautiful camping destination with great amenities.';
                              let cleanDescription = description.replace(/<[^>]*>/g, '');
                              // Remove NBSP and other HTML entities
                              cleanDescription = cleanDescription.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                              // Clean up multiple spaces
                              cleanDescription = cleanDescription.replace(/\s+/g, ' ').trim();
                              const words = cleanDescription.split(' ');
                              const isExpanded = this.state.expandedDescriptions[site.title];
                              
                              if (words.length > 62) {
                                return (
                                  <button 
                                    className="campings__show-more"
                                    onClick={() => this.toggleDescription(site.title)}
                                  >
                                    {isExpanded ? 'Show Less' : 'Show More'}
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {/* All Amenities Stacked */}
                          <div className="campings__amenities-stacked">
                            <h4>Amenities:</h4>
                            <div className="campings__amenities-list">
                              {(() => {
                                const amenitiesText = site.amenities || '';
                                const allAmenities = amenitiesText
                                  .split(/[,.]/)
                                  .map(item => item.trim())
                                  .filter(item => item && item.length > 0);

                                return allAmenities.length > 0 ? (
                                  allAmenities.map((amenity, index) => (
                                    <div key={index} className="campings__amenity-item">
                                      • {amenity}
                                    </div>
                                  ))
                                ) : (
                                  <div className="campings__amenity-item">No amenities listed</div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ) : activeTab[site.title] === 'location' ? (
                        <div className="campings__location-tab">
                          {/* Map Section - Only show if coordinates are valid */}
                          {site.latitude && site.longitude && 
                           site.latitude !== 0 && site.longitude !== 0 && (
                            <div className="campings__map-section">
                              {(() => {
                                // Track map view only once per resort when location tab is opened
                                if (activeTab[site.title] === 'location' && !this.state.imageIndex[`map_${site.title}`]) {
                                  setTimeout(() => {
                                    this.trackEvent(CAMPINGS_VIEW_MAP, `Viewed map for resort: ${site.title}`, site.title);
                                    this.setState(prevState => ({
                                      imageIndex: {
                                        ...prevState.imageIndex,
                                        [`map_${site.title}`]: 1
                                      }
                                    }));
                                  }, 100);
                                }
                                return null;
                              })()}
                              <h4 className="campings__map-title">Location</h4>
                              <div className="campings__map-container">
                                <MapWrapper
                                  locations={[
                                    {
                                      location: {
                                        latitude: site.latitude,
                                        longitude: site.longitude,
                                      },
                                      id: parseInt(site.id.replace('resort-', '')) || 0,
                                    },
                                  ]}
                                  center={Map.getGoogleLocation({
                                    latitude: site.latitude,
                                    longitude: site.longitude,
                                  })}
                                  icon={{
                                    url: MapMarker,
                                    scaledSize: new google.maps.Size(32, 32),
                                    anchor: new google.maps.Point(16, 32),
                                  } as any}
                                  mapOptions={{ 
                                    zoom: 10, 
                                    zoomControl: true, 
                                    disableDefaultUI: false,
                                    mapTypeControl: false,
                                    streetViewControl: true,
                                    fullscreenControl: true
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          <div className="campings__location-info">
                            <div className="campings__detail-item">
                              <strong>Address:</strong>
                              <span>{site.street}</span>
                            </div>
                            <div className="campings__detail-item">
                              <strong>City:</strong>
                              <span>{site.city}</span>
                            </div>
                            <div className="campings__detail-item">
                              <strong>State:</strong>
                              <span>{site.state}</span>
                            </div>
                            <div className="campings__detail-item">
                              <strong>Zip Code:</strong>
                              <span>{site.zipcode}</span>
                            </div>
                            <div className="campings__detail-item">
                              <strong>Phone:</strong>
                              <span>{site.phone}</span>
                            </div>
                            <div className="campings__detail-item">
                              <strong>Country:</strong>
                              <span>{site.country}</span>
                            </div>
                          </div>
                        </div>
                      ) : activeTab[site.title] === 'deal' ? (
                        <div className="campings__deal-tab">
                          <div className="campings__deal-content">
                            <div className="campings__deal-image-container">
                              <img 
                                src={memberDealImage} 
                                alt="Member Deal" 
                                className="campings__deal-image"
                              />
                            </div>
                            <div className="campings__deal-explanation">
                              <h4 className="campings__deal-title">Exclusive Member Deal</h4>
                              <p className="campings__deal-description">
                                As a valued member, you get exclusive access to our premium camping experience at {site.title}. 
                                Use your member code to unlock special rates and benefits that are only available to our members. 
                                Don't miss out on this amazing opportunity to enjoy a first-class camping adventure!
                              </p>
                              <button
                                className="campings__deal-cta-button"
                                onClick={() => this.toggleMemberCodePopup(site.title)}
                              >
                                Get Member Deal
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Website Link with Member Code Button */}
                    <div className="campings__site-website">
                      <div className="campings__website-actions">
            <button
              className="campings__member-code-button"
              onClick={() => this.toggleMemberCodePopup(site.title)}
              title="View member code"
            >
              Member Code
            </button>
                        {site.website ? (
                          <a 
                            href={site.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={() => {
                              this.trackEvent(CAMPINGS_VISIT_WEBSITE, `Visited website for: ${site.title}`, site.title);
                            }}
                          >
                            Visit Website →
                          </a>
                        ) : (
                          <span className="campings__no-website">No Website Available</span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
                ))}
              </div>
            )}

            {!loading && filteredSites.length === 0 && (
              <div className="campings__no-results">
                <p><FormattedMessage id="campings.no_results" defaultMessage="No camping sites found matching your criteria." /></p>
              </div>
            )}
          </div>
        </div>

        {/* Help Tiles Section */}
        <div className="campings__help-tiles-section">
          <div className="container">
            <div className="campings__help-tiles-grid">
              <div 
                className="campings__help-tile"
                onClick={() => {
                  this.props.history.push(`/${accountUsername}${Routes.CampingsFAQs}`);
                }}
              >
                <div className="campings__help-tile-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <h3 className="campings__help-tile-title">
                  <FormattedMessage id="campings.help.faqs.title" defaultMessage="Frequently Asked Questions" />
                </h3>
                <p className="campings__help-tile-text">
                  <FormattedMessage id="campings.help.faqs.description" defaultMessage="Get answers to common questions about Gold & Platinum memberships" />
                </p>
                <div className="campings__help-tile-arrow">→</div>
              </div>

              <div 
                className="campings__help-tile"
                onClick={() => {
                  this.props.history.push(`/${accountUsername}${Routes.CampingsBookingHowTo}`);
                }}
              >
                <div className="campings__help-tile-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <h3 className="campings__help-tile-title">
                  <FormattedMessage id="campings.help.booking.title" defaultMessage="Booking How-To Guide" />
                </h3>
                <p className="campings__help-tile-text">
                  <FormattedMessage id="campings.help.booking.description" defaultMessage="Step-by-step guide to booking your campground stay" />
                </p>
                <div className="campings__help-tile-arrow">→</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }
}

const mapStateToProps = (state: any): IMapStateToProps => ({
  loginStore: state.loginStore,
});

export const Campings = connect(mapStateToProps)(injectIntl(withRouter(CampingsComponent)));
